/**
 * F 340 — the tool EXECUTOR and the permission gate.
 * F 356 — extended with update_node, create_database, create_view.
 *
 * Everything here treats its arguments as UNTRUSTED, because they come from a
 * language model. A prompt is a request, not a permission: the model is told
 * which tools exist in `tools.ts`, and this file decides which of those calls
 * are actually allowed to happen.
 *
 * THE GATE, in the order the checks run (cheap and most-specific first):
 *   1. the tool name must be one of TOOL_NAMES — an unknown name is refused,
 *      and so is `delete_node`, because it has no schema and no entry here;
 *   2. destructive INTENT is refused even when the tool name is legitimate,
 *      because "create_node with a title of delete-everything" is still a
 *      deletion request;
 *   3. for create_node: the kind must be in ALLOWED_KINDS.
 *      For create_view/update_node/create_database: id-style arguments are
 *      validated against the same storage-backed lookup that would write.
 *   4. arguments must satisfy the schema's own required fields and bounds.
 *
 * Checks 1 and 2 are the ones a naive implementation drops, and each of them
 * is a real hole: without 1, any name reaches the executor; without 2, a
 * permitted tool name becomes a Trojan horse for a destructive action.
 *
 * FAIL CLOSED. Every refusal is `{ok:false}` with a reason the Operator can
 * read back to the user. Nothing throws, nothing partially applies, and no
 * branch writes before all of its checks have passed.
 */
import { ALLOWED_KINDS, ALLOWED_COLUMN_TYPES, SEARCHABLE_KINDS, TOOL_NAMES, VIEW_READ_ONLY_KINDS, type AllowedKind, type DatabaseColumnType, type ToolName } from './tools';
import type { QueryLayer } from '../query/query-layer';
import type { StorageAdapter } from '../storage/types';

/** Refusal text the Operator is required to show the user. */
export const DELETION_REFUSAL = 'Deletion is not permitted.';

/**
 * Verbs that mean "destroy something" wherever they appear in an argument.
 *
 * Checked against a lowercased copy of every string in the args. A title is
 * user data, so this is a guard against a confused request, not a security
 * boundary on its own — the boundary is that no delete-shaped TOOL EXISTS
 * to call in the first place.
 */
const DESTRUCTIVE = [
  'delete', 'remove', 'destroy', 'erase', 'wipe', 'purge',
  'drop table', 'truncate', 'rm -rf',
];

export interface ToolContext {
  storage: StorageAdapter;
  query: QueryLayer;
  /** Injected so a test can assert the refresh happened. */
  onChanged?: () => void;
  /** Injected clock, so ids and timestamps are deterministic under test. */
  now?: () => number;
}

export type ToolResult =
  | { ok: true; tool: ToolName; action: string; detail: Record<string, unknown> }
  | { ok: false; tool: string; error: string; message: string };

function refuse(tool: string, error: string, message: string): ToolResult {
  return { ok: false, tool, error, message };
}

function isAllowedKind(v: unknown): v is AllowedKind {
  return typeof v === 'string' && (ALLOWED_KINDS as readonly string[]).includes(v);
}

function isAllowedColumnType(v: unknown): v is DatabaseColumnType {
  return typeof v === 'string' && (ALLOWED_COLUMN_TYPES as readonly string[]).includes(v);
}

function mentionsDeletion(args: Record<string, unknown>): boolean {
  const parts: string[] = [];
  for (const v of Object.values(args)) {
    if (typeof v === 'string') parts.push(v);
  }
  const text = parts.join(' ').toLowerCase();
  return DESTRUCTIVE.some((d) => text.includes(d));
}

function isToolName(v: string): v is ToolName {
  return (TOOL_NAMES as readonly string[]).includes(v);
}

/** Stable id from the title + a counter, so a node named Test is findable. */
function slugId(kind: string, title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return `${kind}_${slug || 'untitled'}`;
}

/**
 * Build a self-contained column factory for the create_database tool. The
 * caller passes `{ name, type }` pairs; this returns objects that satisfy
 * the substrate's PropertyDefinition shape WITHOUT importing the heavy
 * `createXxxProperty` helpers (which would couple the agent layer to the
 * full property system).
 *
 * The choice of icon/color is intentionally minimal — the substrate will
 * accept a property with no options and render it correctly. Columns the
 * AI cannot author (formula, relation, files, person, ...) are not in
 * ALLOWED_COLUMN_TYPES.
 */
function buildProperty(name: string, type: DatabaseColumnType, position: number): Record<string, unknown> {
  return {
    id: `col_${type}_${position}_${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    name,
    type,
    visible: true,
    position,
  };
}

/**
 * Run one tool call. Never throws.
 *
 * `name` is a plain string on purpose — it arrives from a parsed model reply,
 * so it may be anything at all, and the type system cannot vouch for it.
 */
export async function executeTool(
  name: string,
  args: Record<string, unknown>,
  context: ToolContext,
): Promise<ToolResult> {
  const a = args && typeof args === 'object' && !Array.isArray(args) ? args : {};
  const now = context.now ? context.now() : Date.now();

  // ── Gate 1: the name. This is what makes `delete_node` unreachable. ──
  if (name === 'delete_node' || /delete|remove|destroy|erase|wipe|purge/i.test(name)) {
    return refuse(name, 'destructive_tool', DELETION_REFUSAL);
  }
  if (!isToolName(name)) {
    return refuse(
      name,
      'unknown_tool',
      `"${name}" is not a tool the Operator may call. Permitted: ${TOOL_NAMES.join(', ')}.`,
    );
  }

  // ── Gate 2: destructive INTENT behind a legitimate tool name. ──
  if (mentionsDeletion(a)) {
    return refuse(name, 'destructive_intent', DELETION_REFUSAL);
  }

  // ── Gate 3 + 4: per-tool argument validation and execution. ──

  if (name === 'create_node') {
    if (!isAllowedKind(a.kind)) {
      return refuse(name, 'kind_not_allowed',
        `Cannot create a node of kind "${String(a.kind)}". Permitted: ${ALLOWED_KINDS.join(', ')}.`);
    }
    // F 382 — 'view' is in ALLOWED_KINDS so `query_graph` can READ views, but
    // a view is owned by the UI registry and its only legitimate AI write is
    // `create_view` (which checks the parent database exists). Without this,
    // widening ALLOWED_KINDS for reads would silently widen it for writes too,
    // and the agent could mint an orphan view with no database behind it.
    if ((VIEW_READ_ONLY_KINDS as readonly string[]).includes(a.kind)) {
      return refuse(name, 'kind_read_only',
        `"${String(a.kind)}" nodes are created by create_view, not create_node — ` +
        `create_view checks the database exists first.`);
    }
    const title = typeof a.title === 'string' ? a.title.trim() : '';
    if (title.length === 0) {
      return refuse(name, 'title_required', 'A node needs a non-empty title.');
    }
    if (title.length > 200) {
      return refuse(name, 'title_too_long', 'The title is longer than 200 characters.');
    }
    const parentId = typeof a.parentId === 'string' && a.parentId.trim() !== ''
      ? a.parentId.trim()
      : null;

    const id = slugId(a.kind, title);
    // F 340: the key prefix is `${kind}:` because that is what
    // QueryLayer.list() asks the adapter for (query-layer.ts KEY_PREFIX).
    // Inventing a different shape here writes nodes the reader cannot see —
    // the write would succeed and the node would be invisible to the app.
    const key = `${a.kind}:${id}`;
    const existing = await context.storage.load(key).catch(() => null);
    if (existing) {
      return { ok: true, tool: name, action: 'already_exists',
        detail: { kind: a.kind, id, title, key } };
    }

    const entity = {
      id,
      kind: a.kind,
      title,
      parentId,
      createdAt: new Date(now).toISOString(),
      updatedAt: new Date(now).toISOString(),
    };
    await context.storage.save(key, entity);

    // Only a real write announces itself, so the UI refresh cannot be faked
    // by a call that was refused.
    context.onChanged?.();

    return { ok: true, tool: name, action: 'created', detail: { kind: a.kind, id, title, key } };
  }

  if (name === 'query_graph') {
    if (!isAllowedKind(a.kind)) {
      return refuse(name, 'kind_not_allowed',
        `Cannot read nodes of kind "${String(a.kind)}". Permitted: ${ALLOWED_KINDS.join(', ')}.`);
    }
    const rawLimit = a.limit;
    const limit = typeof rawLimit === 'number' && Number.isFinite(rawLimit) ? rawLimit : 10;
    if (limit < 1 || limit > 100) {
      return refuse(name, 'limit_out_of_range', 'limit must be between 1 and 100.');
    }
    const sortBy = a.sortBy === 'title' ? 'title' : 'createdAt';

    // F 382 — 'view' is NOT an entity kind, so QueryLayer.list() cannot read
    // it: that asks the adapter for `view:` and the UI registry writes
    // `freshcards:views:<databaseId>`. Two other reasons not to route it
    // through the entity path: a view array is ONE key holding N views (not
    // N keyed rows), and the UI's `filter` field is a grouped OR
    // (`PropertyFilter`) which the entity serializer would mis-shape.
    //
    // So views are read straight from the registry key, and ONLY identity +
    // layout are returned. `filter` is deliberately omitted: F 379 measured
    // that the UI `PropertyFilter` (grouped OR) and core `FilterRule[]` (flat
    // AND) are not interchangeable, and that flattening one into the other
    // silently returns zero rows. Handing the model a filter shape the UI
    // cannot honour would be worse than not returning it.
    if (a.kind === 'view') {
      const wanted = a.filter && typeof a.filter === 'object'
        ? String((a.filter as Record<string, unknown>).databaseId ?? '').trim()
        : '';
      const keys = await context.storage.list('freshcards:views:').catch(() => [] as string[]);
      let all: Array<{ databaseId: string; name: string; layout: string }> = [];
      for (const k of keys) {
        const databaseId = k.slice('freshcards:views:'.length);
        if (wanted && databaseId !== wanted) continue;
        const arr = await context.storage.load(k).catch(() => null);
        if (!Array.isArray(arr)) continue;
        for (const v of arr as Array<Record<string, unknown>>) {
          all.push({
            databaseId: String(v.databaseId ?? databaseId),
            name: String(v.name ?? ''),
            layout: String(v.layout ?? v.type ?? ''),
          });
        }
      }
      const sortedViews = sortBy === 'title'
        ? all.sort((x, y) => x.name.localeCompare(y.name))
        : all;
      const page = sortedViews.slice(0, limit);
      return {
        ok: true,
        tool: name,
        action: 'listed',
        detail: { kind: 'view', limit, sortBy, count: page.length, nodes: page },
      };
    }

    const rows = await context.query.list<Record<string, unknown>>(a.kind, { limit: 100 });
    const sorted = [...rows].sort((x, y) => {
      if (sortBy === 'title') {
        return String(x.title ?? '').localeCompare(String(y.title ?? ''));
      }
      return String(x.createdAt ?? '').localeCompare(String(y.createdAt ?? ''));
    });
    const page = sorted.slice(0, limit);
    return {
      ok: true,
      tool: name,
      action: 'listed',
      detail: { kind: a.kind, limit, sortBy, count: page.length,
        nodes: page.map((e) => ({ id: e.id, title: e.title })) },
    };
  }

  if (name === 'update_node') {
    const id = typeof a.id === 'string' ? a.id.trim() : '';
    if (id.length === 0) {
      return refuse(name, 'id_required', 'update_node needs the id of an existing node.');
    }
    const title = typeof a.title === 'string' ? a.title.trim() : '';
    const hasTitle = title.length > 0;
    if (hasTitle && title.length > 200) {
      return refuse(name, 'title_too_long', 'The title is longer than 200 characters.');
    }
    const content = typeof a.content === 'string' ? a.content : null;
    if (!hasTitle && content === null) {
      return refuse(name, 'no_fields_to_update',
        'update_node needs at least one of `title` or `content` to change.');
    }

    // F 356 — find the node across all known kinds. The substrate stores
    // nodes by `${kind}:${id}`; we don't know the kind from the id alone,
    // so we try each SEARCHABLE_KIND. The first hit wins. The list is
    // wider than ALLOWED_KINDS on purpose — the AI may have created a
    // database via create_database (kind='database'), and that node is
    // reachable by update_node's id lookup even though 'database' is not
    // in ALLOWED_KINDS.
    const possibleKeys: string[] = [];
    for (const k of SEARCHABLE_KINDS) {
      possibleKeys.push(`${k}:${id}`);
    }

    let foundKey: string | null = null;
    let foundEntity: Record<string, unknown> | null = null;
    for (const k of possibleKeys) {
      const e = await context.storage.load(k).catch(() => null);
      if (e && typeof e === 'object') {
        foundKey = k;
        foundEntity = e as Record<string, unknown>;
        break;
      }
    }
    if (!foundKey || !foundEntity) {
      return refuse(name, 'not_found',
        `No node with id "${id}" in any of the searchable kinds (${SEARCHABLE_KINDS.join(', ')}).`);
    }

    const updated: Record<string, unknown> = { ...foundEntity };
    if (hasTitle) updated.title = title;
    if (content !== null) {
      // F 356 — content is a side effect on the page content key, not the
      // metadata key. The substrate stores page bodies under
      // `freshcards:content:${id}` (CONTENT_KEY_PREFIX in
      // src/core/workspace/pageContentStorage.ts); the executor writes
      // there when content is present, and the metadata updatedAt is
      // bumped either way. The shape `{ markdown }` is what the rest of
      // the app reads; see pageContentStorage.ts:65-90 for the matching
      // envelope shape.
      await context.storage.save(`freshcards:content:${id}`, { markdown: content });
    }
    updated.updatedAt = new Date(now).toISOString();
    await context.storage.save(foundKey, updated);

    context.onChanged?.();

    return { ok: true, tool: name, action: 'updated',
      detail: { id, key: foundKey, hadTitle: hasTitle, hadContent: content !== null } };
  }

  if (name === 'create_database') {
    // F 356 — `kind: 'database'` is the SAME page-kind the substrate uses
    // when WorkspaceContext.createPage(title, parent, 'database') runs.
    // create_node already permits kind='database' (see ALLOWED_KINDS above),
    // so a direct create_node(kind='database', title) call writes a
    // database-shaped page. For the AI, this richer call writes BOTH the
    // page metadata AND a database schema in the substrate's expected
    // shape, so the created database renders with the requested columns.
    const title = typeof a.title === 'string' ? a.title.trim() : '';
    if (title.length === 0) {
      return refuse(name, 'title_required', 'A database needs a non-empty title.');
    }
    if (title.length > 200) {
      return refuse(name, 'title_too_long', 'The title is longer than 200 characters.');
    }
    if (!Array.isArray(a.columns) || a.columns.length === 0) {
      return refuse(name, 'columns_required',
        'create_database needs at least one column.');
    }
    if (a.columns.length > 32) {
      return refuse(name, 'too_many_columns', 'create_database allows at most 32 columns.');
    }

    const validatedColumns: Array<{ name: string; type: DatabaseColumnType }> = [];
    for (let i = 0; i < a.columns.length; i++) {
      const c = a.columns[i] as Record<string, unknown>;
      if (!c || typeof c !== 'object' || Array.isArray(c)) {
        return refuse(name, 'column_shape',
          `columns[${i}] must be an object with name and type.`);
      }
      const colName = typeof c.name === 'string' ? c.name.trim() : '';
      if (colName.length === 0 || colName.length > 64) {
        return refuse(name, 'column_name',
          `columns[${i}].name must be a non-empty string up to 64 chars.`);
      }
      if (!isAllowedColumnType(c.type)) {
        return refuse(name, 'column_type_not_allowed',
          `columns[${i}].type "${String(c.type)}" is not permitted. Allowed: ${ALLOWED_COLUMN_TYPES.join(', ')}.`);
      }
      validatedColumns.push({ name: colName, type: c.type });
    }

    const id = slugId('database', title);
    const key = `database:${id}`;

    // The substrate shape: a database is itself stored under the
    // `database:` kind prefix (mirroring how WorkspaceContext stores
    // database pages), with a `properties` array carrying the schema.
    // We build a minimal but valid Database record.
    const dbEntity = {
      id,
      kind: 'database',
      title,
      parentId: null,
      properties: validatedColumns.map((c, i) => buildProperty(c.name, c.type, i)),
      items: [],
      createdAt: new Date(now).toISOString(),
      updatedAt: new Date(now).toISOString(),
    };
    await context.storage.save(key, dbEntity);
    context.onChanged?.();

    return { ok: true, tool: name, action: 'created',
      detail: { id, title, key, columnCount: validatedColumns.length, columns: validatedColumns } };
  }

  if (name === 'create_view') {
    const databaseId = typeof a.databaseId === 'string' ? a.databaseId.trim() : '';
    if (databaseId.length === 0) {
      return refuse(name, 'databaseId_required', 'create_view needs databaseId.');
    }
    const viewName = typeof a.name === 'string' ? a.name.trim() : '';
    if (viewName.length === 0 || viewName.length > 64) {
      return refuse(name, 'name_required',
        'create_view needs a non-empty name up to 64 chars.');
    }
    const rawType = a.type;
    if (rawType !== 'gallery' && rawType !== 'kanban' && rawType !== 'list') {
      return refuse(name, 'type_not_allowed',
        `create_view type must be one of gallery, kanban, list. Got "${String(rawType)}".`);
    }

    // The database must exist as a database-kind node. The view key prefix
    // (`freshcards:views:${databaseId}`) is a different store, but the view
    // is meaningless without its parent, so we verify the parent exists.
    let dbExists = false;
    for (const k of [`database:${databaseId}`, `page:${databaseId}`]) {
      const e = await context.storage.load(k).catch(() => null);
      if (e && typeof e === 'object') { dbExists = true; break; }
    }
    if (!dbExists) {
      return refuse(name, 'database_not_found',
        `No database with id "${databaseId}".`);
    }

    const viewKey = `freshcards:views:${databaseId}`;
    const existing = await context.storage.load(viewKey).catch(() => null);
    const currentViews: Array<Record<string, unknown>> = Array.isArray(existing)
      ? (existing as Array<Record<string, unknown>>)
      : [];
    const view: Record<string, unknown> = {
      id: `view-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      databaseId,
      name: viewName,
      layout: rawType,
      filter: {},
      sort: [],
      visiblePropertyIds: [],
      propertyOrder: [],
      createdAt: new Date(now).toISOString(),
      updatedAt: new Date(now).toISOString(),
    };
    await context.storage.save(viewKey, [...currentViews, view]);
    context.onChanged?.();

    return { ok: true, tool: name, action: 'created',
      detail: { databaseId, viewId: view.id, name: viewName, layout: rawType } };
  }

  // Should be unreachable — isToolName is the gate, but defensive.
  return refuse(name, 'unhandled_tool', `Executor has no handler for "${name}".`);
}